import { device } from './device.js';
import { catalog } from './catalog.js';

/* Enlaces directos y sincronización de la barra de direcciones (solo web).
   - /movie/UID, /series/UID, /live, /movies, /search… abren esa pantalla al cargar.
   - /watch/vod/UID, /watch/series/UID/T/E y /watch/live/UID abren además el reproductor, retomando
     la posición guardada: al pulsar F5 viendo una película, vuelve a verse donde estaba.
   Los UID son identificadores opacos (32 hex, catalog.uidOf) en lugar de los ID del proveedor.
   El router de Framework7 no toca la URL; aquí se refleja con replaceState (sin crear historial). */

export function urlSyncEnabled() {
    return /^https?:$/.test(location.protocol) && !device.isCordova && !device.isElectron;
}

/* La web también vive en filoteca.com/app (la raíz es la landing): Apache quita ese prefijo antes de llegar a Node.
   Se mantiene en la barra de direcciones al sincronizarla; los enlaces antiguos sin prefijo (/movie/…) siguen valiendo. */
export function appBase() {
    try { return /^\/app(\/|$)/.test(location.pathname) ? '/app' : ''; } catch (e) { return ''; }
}

/** Ruta de la app sin el prefijo /app: «/app/movie/x» → «/movie/x», «/app» → «/». */
export function appPath(pathname) {
    return String(pathname == null ? (typeof location !== 'undefined' ? location.pathname : '/') : pathname).replace(/^\/app(?=\/|$)/, '') || '/';
}

/** URL que representa lo que el reproductor está reproduciendo, o null. */
export function watchUrl(player) {
    if (!player || !player.isOpen) return null;
    if (player.mode === 'vod' && player.item) return '/watch/vod/' + catalog.uidOf('vod', player.item.id);
    if (player.mode === 'episode' && player.series && player.episode) {
        return '/watch/series/' + catalog.uidOf('series', player.series.id) + '/' + player.episode.season + '/' + player.episode.num;
    }
    if (player.mode === 'live' && player.item) return '/watch/live/' + catalog.uidOf('live', player.item.id);
    return null;
}

/** Ruta del router → URL pública (fichas con UID en lugar del ID del proveedor). */
export function publicUrl(routeUrl) {
    const clean = String(routeUrl || '/').replace(/\/+(\?|$)/, '$1') || '/';
    let m;
    if ((m = /^\/movie\/([^/?]+)$/.exec(clean))) return '/movie/' + catalog.uidOf('vod', decodeURIComponent(m[1]));
    if ((m = /^\/series\/([^/?]+)$/.exec(clean))) return '/series/' + catalog.uidOf('series', decodeURIComponent(m[1]));
    return clean;
}

/** Refleja `url` (ruta del router o de reproducción) en la barra de direcciones. */
export function syncUrl(url) {
    if (!urlSyncEnabled() || !url) return;
    const clean = appBase() + publicUrl(url);
    if (location.pathname + location.search === clean) return;
    try {
        history.replaceState(history.state, '', clean);
    } catch (e) { /* entornos sin History API */ }
}

/**
 * Interpreta la URL con la que se abrió la app. Devuelve null si no es un enlace conocido, o
 * { route } para secciones, o { kind, token, season?, num?, play } para fichas y reproducción
 * (token: UID opaco o, por compatibilidad, ID del proveedor; se resuelve con el catálogo cargado).
 */
export function parseEntryUrl(pathname, search = '') {
    const p = appPath(pathname || '/').replace(/\/+$/, '');
    let m;
    const tok = (s) => decodeURIComponent(s);
    if ((m = /^\/watch\/vod\/([^/]+)$/.exec(p))) return { kind: 'vod', token: tok(m[1]), play: true };
    if ((m = /^\/watch\/series\/([^/]+)\/(\d+)\/(\d+)$/.exec(p))) return { kind: 'series', token: tok(m[1]), season: Number(m[2]), num: Number(m[3]), play: true };
    if ((m = /^\/watch\/live\/([^/]+)$/.exec(p))) return { kind: 'live', token: tok(m[1]), play: true };
    if ((m = /^\/movie\/([^/]+)$/.exec(p))) return { kind: 'vod', token: tok(m[1]), play: false };
    if ((m = /^\/series\/([^/]+)$/.exec(p))) return { kind: 'series', token: tok(m[1]), play: false };
    if ((m = /^\/actor\/([^/]+)$/.exec(p))) return { route: '/actor/' + m[1] + '/' };
    if (p === '/search') {
        // Enlace de «compartir»: /search/?q=título abre el buscador con eso escrito.
        const q = (new URLSearchParams(search).get('q') || '').slice(0, 120);
        return { route: '/search/' + (q ? '?q=' + encodeURIComponent(q) : '') };
    }
    if (/^\/(live|movies|series|mylist|settings|guide|recordings|offline)$/.test(p)) return { route: p + '/' };
    return null;
}

/** Con el catálogo cargado: UID (o ID antiguo) → ID del proveedor, o null si no existe. */
export function resolveToken(kind, token) {
    const hit = catalog.byUid(token);
    if (hit) return hit.kind === kind ? hit.id : null;
    return catalog.get(kind, token) ? token : null;
}

/** Espera a que el catálogo esté cargado (o se rinde pasado el tiempo). */
export function whenCatalogLoaded(timeoutMs = 90000) {
    if (catalog.loaded) return Promise.resolve(true);
    return new Promise((resolve) => {
        const off = catalog.on('change', () => {
            if (!catalog.loaded) return;
            off();
            clearTimeout(timer);
            resolve(true);
        });
        const timer = setTimeout(() => { off(); resolve(false); }, timeoutMs);
    });
}
